import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { API_URL } from '../config/api'

const BACKEND_API_URL = API_URL

// Frontend Position interface (aligned with backend PositionInfoDTO)
export interface PositionSkill {
  name: string
  matchScore: number
}

export interface PositionDesiredAttribute {
  key: string
  percentage: number
}

export interface Position {
  id: string
  companyId?: string
  jobTitle?: string
  departmentId: string
  department: string
  unit?: string
  team?: string
  seniorityLevel?: string
  // NEW BACKEND-ALIGNED FIELDS (optional to allow legacy fields)
  location?: {
    country?: string
    city?: string
    state?: string
    address?: string
    district?: string
  }
  typeOfPosition?: string | null
  workingMethod?: string | null
  sponsorshipSupport?: string | null
  hiringUrgency?: string | null
  salaryExpectation?: {
    value1?: string
    value2?: string
    unit?: string
  }
  showSalaryToTalent?: boolean | null
  trainingOpportunities?: string[] | null
  physicalConditions?: string[] | null
  workHoursArrangement?: string[] | null
  responsibilities?: string | null
  desiredAttributes1?: PositionDesiredAttribute[] | null
  desiredAttributes2?: PositionDesiredAttribute[] | null
  // LEGACY FIELDS (backward compatibility with forms)
  workType?: string | null
  employeeLocation?: string | null
  city?: string | null
  state?: string | null
  district?: string | null
  employmentType?: string | null
  minSalary?: string | null
  maxSalary?: string | null
  visaSponsorship?: string | null
  urgentHiring?: string | null
  education?: string[] | null
  workSchedule?: string | null
  teamResponsibilities?: string | null
  // Common fields - skills with scores
  skills: PositionSkill[]
  benefits: string[]
  employmentScope?: string | null
  aboutUs?: string | null
  requirements?: string | null
  currency?: string | null
  whatWeOffer?: string | null
  status?: string | null
}

// Backend request format
interface CreatePositionRequest {
  companyId: string
  departmentId: string
  unit?: string
  team?: string
  jobTitle: string
  seniorityLevel?: string
  location?: {
    country?: string
    state?: string
    city?: string
    district?: string
    address?: string
  }
  typeOfPosition?: string
  workingMethod?: string
  sponsorshipSupport?: string
  hiringUrgency?: string
  internalExternal?: string
  salaryExpectation?: {
    value1?: string
    value2?: string
    unit?: string
    rate?: number
  }
  showSalaryToTalent?: boolean
  skills?: { name: string; point: number }[]
  benefits?: string[]
  trainingOpportunities?: string[]
  physicalConditions?: string[]
  workHoursArrangement?: string[]
  employmentScope?: string
  desiredAttributes1?: PositionDesiredAttribute[]
  desiredAttributes2?: PositionDesiredAttribute[]
  aboutUs?: string
  responsibilities?: string
  requirements?: string
  whatWeOffer?: string
  status?: string
}

// Update position request format
interface UpdatePositionRequest {
  positionId: string
  positionInfo: CreatePositionRequest
}

interface PositionState {
  positions: Position[]
  loading: boolean
  error: string | null
}

const initialState: PositionState = {
  positions: [],
  loading: false,
  error: null
}

const normalizeCurrencyIdentifier = (value?: string | null): string => {
  if (!value) return 'usd'
  const normalized = value.trim().toLowerCase()
  if (normalized === 'tl' || normalized === 'try') return 'try'
  if (normalized === 'usd') return 'usd'
  if (normalized === 'eur') return 'eur'
  return normalized
}

// Helper: Map frontend Position to backend request format
const mapPositionToBackend = (position: Position, companyId: string, departmentId: string): CreatePositionRequest => {
  // Use new field names if available, fallback to legacy fields
  const locationCountry = position.location?.country || position.employeeLocation || ''
  const locationCity = position.location?.city || position.city || ''
  const locationState = position.location?.state || position.state || ''
  const normalizedSalaryUnit = normalizeCurrencyIdentifier(
    position.salaryExpectation?.unit || position.currency
  )
  const salaryExpectation = {
    value1: position.salaryExpectation?.value1 || position.minSalary || '0',
    value2: position.salaryExpectation?.value2 || position.maxSalary || '0',
    unit: normalizedSalaryUnit,
    rate: 0
  }

  return {
    companyId,
    departmentId,
    unit: position.unit,
    team: position.team,
    jobTitle: position.jobTitle || '',
    seniorityLevel: position.seniorityLevel,
    location: {
      country: locationCountry,
      city: locationCity,
      state: locationState,
      address: position.location?.address || '',
      district: position.location?.district || position.district || ''
    },
    typeOfPosition: position.typeOfPosition || position.employmentType || '',
    workingMethod: position.workingMethod || position.workType || '',
    sponsorshipSupport: position.sponsorshipSupport || position.visaSponsorship || '',
    hiringUrgency: position.hiringUrgency || position.urgentHiring || '',
    salaryExpectation,
    showSalaryToTalent: position.showSalaryToTalent ?? true,
    skills: (position.skills || []).map(skill => ({
      id: Array.from({length: 24}, () => Math.floor(Math.random() * 16).toString(16)).join(''),
      name: skill.name,
      text: skill.name,
      point: skill.matchScore
    })),
    benefits: position.benefits || [],
    trainingOpportunities: position.trainingOpportunities || position.education || [],
    physicalConditions: position.physicalConditions || [],
    workHoursArrangement: position.workHoursArrangement || (position.workSchedule ? [position.workSchedule] : []),
    desiredAttributes1: position.desiredAttributes1 || [],
    desiredAttributes2: position.desiredAttributes2 || [],
    employmentScope: position.employmentScope ?? undefined,
    aboutUs: position.aboutUs || '',
    responsibilities: (position.responsibilities || position.teamResponsibilities) ?? '',
    requirements: position.requirements || '',
    whatWeOffer: position.whatWeOffer ?? undefined,
    status: position.status ?? undefined
  }
}

// Helper: Map backend response to frontend Position format
const mapBackendToPosition = (backendData: any): Position => {
  return {
    id: backendData.id || backendData.positionId || '',
    companyId: backendData.companyId,
    jobTitle: backendData.jobTitle || '',
    departmentId: backendData.departmentId || '',
    department: backendData.departmentId || '',
    unit: backendData.unit,
    team: backendData.team,
    seniorityLevel: backendData.seniorityLevel || '',
    // New backend-aligned fields
    location: {
      country: backendData.location?.country || '',
      city: backendData.location?.city || '',
      state: backendData.location?.state || '',
      address: backendData.location?.address || '',
      district: backendData.location?.district || ''
    },
    typeOfPosition: backendData.typeOfPosition || '',
    workingMethod: backendData.workingMethod || '',
    sponsorshipSupport: backendData.sponsorshipSupport || '',
    hiringUrgency: backendData.hiringUrgency || '',
    salaryExpectation: backendData.salaryExpectation ? {
      value1: backendData.salaryExpectation.value1 || '',
      value2: backendData.salaryExpectation.value2 || '',
      unit: normalizeCurrencyIdentifier(backendData.salaryExpectation.unit)
    } : undefined,
    showSalaryToTalent: backendData.showSalaryToTalent ?? true,
    skills: (backendData.skills || []).map((skill: any) => ({
      name: skill.name || '',
      matchScore: skill.point || 0
    })),
    benefits: backendData.benefits || [],
    trainingOpportunities: backendData.trainingOpportunities,
    physicalConditions: backendData.physicalConditions,
    workHoursArrangement: backendData.workHoursArrangement,
    desiredAttributes1: backendData.desiredAttributes1 || [],
    desiredAttributes2: backendData.desiredAttributes2 || [],
    employmentScope: backendData.employmentScope,
    aboutUs: backendData.aboutUs,
    responsibilities: backendData.responsibilities,
    requirements: backendData.requirements,
    // Extra fields
    currency: normalizeCurrencyIdentifier(backendData.salaryExpectation?.unit),
    whatWeOffer: backendData.whatWeOffer,
    status: backendData.status || 'Draft',
    // Legacy fields for backward compatibility
    workType: backendData.workingMethod || '',
    employeeLocation: backendData.location?.country || '',
    city: backendData.location?.city || null,
    state: backendData.location?.state || null,
    district: backendData.location?.district || null,
    employmentType: backendData.typeOfPosition || '',
    minSalary: backendData.salaryExpectation?.value1 || '',
    maxSalary: backendData.salaryExpectation?.value2 || '',
    visaSponsorship: backendData.sponsorshipSupport || '',
    urgentHiring: backendData.hiringUrgency || '',
    education: backendData.trainingOpportunities,
    workSchedule: backendData.workHoursArrangement?.[0] || '',
    teamResponsibilities: backendData.responsibilities
  }
}

export const fetchPositions = createAsyncThunk(
  'positions/fetchPositions',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } }
      const token = state.auth.token
      const companyId = state.auth.user?.companyId || state.auth.user?.id

      if (!token) {
        return rejectWithValue('No authentication token found')
      }

      if (!companyId) {
        return rejectWithValue('No company ID found')
      }

      // Fetch all departments first
      const deptResponse = await axios.get(
        `${BACKEND_API_URL}/company/listdepartments/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const departments = deptResponse.data

      // Fetch positions for each department in parallel
      const positionPromises = departments.map((dept: any) =>
        axios.get(
          `${BACKEND_API_URL}/position/listpositions/${companyId}/${dept.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      )

      const results = await Promise.all(positionPromises)
      const allPositions = results.flatMap(r => r.data).map(mapBackendToPosition)

      return allPositions
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch positions')
    }
  }
)

export const fetchPositionsByDepartment = createAsyncThunk(
  'positions/fetchPositionsByDepartment',
  async (departmentId: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } }
      const token = state.auth.token
      const companyId = state.auth.user?.companyId || state.auth.user?.id

      if (!token) {
        return rejectWithValue('No authentication token found')
      }

      if (!companyId) {
        return rejectWithValue('No company ID found')
      }

      const response = await axios.get(
        `${BACKEND_API_URL}/position/listpositions/${companyId}/${departmentId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      // Map backend response to frontend format
      return response.data.map(mapBackendToPosition)
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch positions')
    }
  }
)

export const fetchPositionById = createAsyncThunk(
  'positions/fetchPositionById',
  async ({ departmentId, positionId }: { departmentId: string; positionId: string }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } }
      const token = state.auth.token
      const companyId = state.auth.user?.companyId || state.auth.user?.id

      if (!token) {
        return rejectWithValue('No authentication token found')
      }

      const response = await axios.get(
        `${BACKEND_API_URL}/position/getposition/${companyId}/${departmentId}/${positionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      return mapBackendToPosition(response.data)
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch position')
    }
  }
)

export const createPosition = createAsyncThunk(
  'positions/createPosition',
  async (payload: { position: Position; departmentId: string }, { rejectWithValue, getState }) => {
    try {
      const { position, departmentId } = payload
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } }
      const token = state.auth.token
      const companyId = state.auth.user?.companyId || state.auth.user?.id

      if (!token) {
        return rejectWithValue('No authentication token found')
      }

      if (!companyId) {
        return rejectWithValue('No company ID found')
      }

      if (!departmentId) {
        return rejectWithValue('No department ID found')
      }

      // Map to backend format - include ALL fields including companyId and departmentId
      const requestData = mapPositionToBackend(position, companyId, departmentId)

      console.log('📤 Sending to backend API:', JSON.stringify(requestData, null, 2))

      const response = await axios.post(
        `${BACKEND_API_URL}/position/createposition`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      // Backend returns 200 with ID in response body, or 204 with ID in Location header
      const newId = response.data || response.headers?.['location']?.split('/')?.pop() || `temp-${Date.now()}`

      return {
        ...position,
        id: newId,
        departmentId,
        department: departmentId
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create position')
    }
  }
)

export const updatePosition = createAsyncThunk(
  'positions/updatePosition',
  async (payload: { position: Position; departmentId: string }, { rejectWithValue, getState }) => {
    try {
      const { position, departmentId } = payload
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } }
      const token = state.auth.token
      const companyId = state.auth.user?.companyId || state.auth.user?.id

      if (!token) {
        return rejectWithValue('No authentication token found')
      }

      if (!companyId) {
        return rejectWithValue('No company ID found')
      }

      if (!departmentId) {
        return rejectWithValue('No department ID found')
      }

      if (!position.id) {
        return rejectWithValue('No position ID found')
      }

      // Map to backend format and wrap in UpdatePositionRequest structure
      const positionInfo = mapPositionToBackend(position, companyId, departmentId)
      const requestData: UpdatePositionRequest = {
        positionId: position.id,
        positionInfo
      }

      await axios.put(
        `${BACKEND_API_URL}/position/updateposition`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      return {
        ...position,
        departmentId,
        department: departmentId
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update position')
    }
  }
)

export const deletePosition = createAsyncThunk(
  'positions/deletePosition',
  async ({ departmentId, positionId }: { departmentId: string; positionId: string }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } }
      const token = state.auth.token
      const companyId = state.auth.user?.companyId || state.auth.user?.id

      if (!token) {
        return rejectWithValue('No authentication token found')
      }

      await axios.delete(
        `${BACKEND_API_URL}/position/deleteposition/${companyId}/${departmentId}/${positionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      return positionId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete position')
    }
  }
)

export const duplicatePosition = createAsyncThunk(
  'positions/duplicatePosition',
  async (payload: { position: Position; departmentId: string }, { rejectWithValue, getState }) => {
    try {
      const { position: positionToDuplicate, departmentId } = payload
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } }
      const token = state.auth.token
      const companyId = state.auth.user?.companyId || state.auth.user?.id

      if (!token) {
        return rejectWithValue('No authentication token found')
      }

      if (!companyId) {
        return rejectWithValue('No company ID found')
      }

      if (!departmentId) {
        return rejectWithValue('No department ID found')
      }

      const fullPositionResponse = await axios.get(
        `${BACKEND_API_URL}/position/getposition/${companyId}/${departmentId}/${positionToDuplicate.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      const fullPosition = mapBackendToPosition(fullPositionResponse.data)

      // Ensure ALL fields have data for backend PositionInfoDTO
      const newPositionData: Position = {
        ...fullPosition,
        jobTitle: `${fullPosition.jobTitle} (Copy)`,
        id: '', // Empty ID for new position
        departmentId: departmentId, // Explicitly set departmentId from function parameter
        department: departmentId, // Explicitly set department from function parameter
        // Required fields with defaults if missing (use new field names)
        team: fullPosition.team || 'General',
        unit: fullPosition.unit || 'Main',
        seniorityLevel: fullPosition.seniorityLevel || 'midlevel',
        location: fullPosition.location || {
          country: fullPosition.employeeLocation || 'tr',
          city: fullPosition.city || '',
          state: fullPosition.state || '',
          address: ''
        },
        typeOfPosition: fullPosition.typeOfPosition || fullPosition.employmentType || 'fulltime',
        workingMethod: fullPosition.workingMethod || fullPosition.workType || 'onsite',
        sponsorshipSupport: fullPosition.sponsorshipSupport || fullPosition.visaSponsorship || 'No',
        hiringUrgency: fullPosition.hiringUrgency || fullPosition.urgentHiring || 'norush',
        salaryExpectation: fullPosition.salaryExpectation || {
          value1: fullPosition.minSalary || '0',
          value2: fullPosition.maxSalary || '0',
          unit: normalizeCurrencyIdentifier(fullPosition.currency)
        },
        showSalaryToTalent: fullPosition.showSalaryToTalent ?? true,
        employmentScope: fullPosition.employmentScope || 'internal',
        // Text fields - if empty, use placeholder (use new field names)
        aboutUs: fullPosition.aboutUs?.trim() || 'TBD',
        responsibilities: fullPosition.responsibilities || fullPosition.teamResponsibilities?.trim() || 'TBD',
        requirements: fullPosition.requirements?.trim() || 'TBD',
        // Arrays - if empty, use empty array
        skills: fullPosition.skills || [],
        benefits: fullPosition.benefits || [],
        trainingOpportunities: fullPosition.trainingOpportunities || fullPosition.education || [],
        physicalConditions: fullPosition.physicalConditions || [],
        workHoursArrangement: fullPosition.workHoursArrangement || (fullPosition.workSchedule ? [fullPosition.workSchedule] : []),
        desiredAttributes1: fullPosition.desiredAttributes1 || [],
        desiredAttributes2: fullPosition.desiredAttributes2 || [],
        // Other fields
        whatWeOffer: fullPosition.whatWeOffer || '',
        status: fullPosition.status || 'Draft'
      }

      // Map to backend format - returns PositionInfoDTO structure
      const mappedPosition = mapPositionToBackend(newPositionData, companyId, departmentId)

      // Backend expects PositionInfoDTO directly in request body
      // Include all fields from mappedPosition (companyId, departmentId, and all position fields)
      const requestData = {
        companyId: mappedPosition.companyId,
        departmentId: mappedPosition.departmentId,
        unit: mappedPosition.unit,
        team: mappedPosition.team,
        jobTitle: mappedPosition.jobTitle,
        seniorityLevel: mappedPosition.seniorityLevel,
        location: {
          country: mappedPosition.location?.country || '',
          state: mappedPosition.location?.state || '',
          city: mappedPosition.location?.city || '',
          district: mappedPosition.location?.district || undefined,
          address: mappedPosition.location?.address || ''
        },
        typeOfPosition: mappedPosition.typeOfPosition,
        workingMethod: mappedPosition.workingMethod,
        sponsorshipSupport: mappedPosition.sponsorshipSupport,
        hiringUrgency: mappedPosition.hiringUrgency,
        salaryExpectation: mappedPosition.salaryExpectation || {
          value1: '0',
          value2: '0',
          unit: 'usd',
          rate: 0
        },
        showSalaryToTalent: mappedPosition.showSalaryToTalent ?? true,
        skills: mappedPosition.skills || [],
        benefits: mappedPosition.benefits || [],
        trainingOpportunities: mappedPosition.trainingOpportunities || [],
        physicalConditions: mappedPosition.physicalConditions || [],
        workHoursArrangement: mappedPosition.workHoursArrangement || [],
        desiredAttributes1: mappedPosition.desiredAttributes1 || [],
        desiredAttributes2: mappedPosition.desiredAttributes2 || [],
        employmentScope: mappedPosition.employmentScope || 'internal',
        aboutUs: mappedPosition.aboutUs || 'TBD',
        responsibilities: mappedPosition.responsibilities || 'TBD',
        requirements: mappedPosition.requirements || 'TBD',
        whatWeOffer: mappedPosition.whatWeOffer || undefined,
        status: mappedPosition.status || undefined
      }

      const response = await axios.post(
        `${BACKEND_API_URL}/position/createposition`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      // Backend returns 200 with ID in response body, or 204 with ID in Location header
      const newId = response.data || response.headers?.['location']?.split('/')?.pop() || `temp-${Date.now()}`

      return {
        ...newPositionData,
        id: newId
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to duplicate position')
    }
  }
)

const positionSlice = createSlice({
  name: 'positions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    updateLocalPosition: (state, action: PayloadAction<Position>) => {
      const index = state.positions.findIndex((p) => p.id === action.payload.id)
      if (index !== -1) {
        state.positions[index] = {
          ...state.positions[index],
          ...action.payload,
          benefits: action.payload.benefits || []
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPositions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPositions.fulfilled, (state, action: PayloadAction<Position[]>) => {
        state.loading = false
        state.positions = action.payload
      })
      .addCase(fetchPositions.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) ?? 'Failed to fetch positions'
      })
      .addCase(fetchPositionsByDepartment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPositionsByDepartment.fulfilled, (state, action: PayloadAction<Position[]>) => {
        state.loading = false
        // Replace positions with fetched department positions
        state.positions = action.payload
      })
      .addCase(fetchPositionsByDepartment.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) ?? 'Failed to fetch positions'
      })
      .addCase(fetchPositionById.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchPositionById.fulfilled, (state, action: PayloadAction<Position>) => {
        state.loading = false
        const index = state.positions.findIndex((pos) => pos.id === action.payload.id)
        if (index !== -1) {
          state.positions[index] = action.payload
        } else {
          state.positions.push(action.payload)
        }
      })
      .addCase(fetchPositionById.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) ?? 'Failed to fetch position'
      })
      .addCase(createPosition.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createPosition.fulfilled, (state, action: PayloadAction<Position>) => {
        state.loading = false
        state.positions.push(action.payload)
      })
      .addCase(createPosition.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) ?? 'Failed to create position'
      })
      .addCase(updatePosition.pending, (state) => {
        state.loading = true
      })
      .addCase(updatePosition.fulfilled, (state, action: PayloadAction<Position>) => {
        state.loading = false
        const index = state.positions.findIndex((pos) => pos.id === action.payload.id)
        if (index !== -1) {
          state.positions[index] = action.payload
        }
      })
      .addCase(updatePosition.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) ?? 'Failed to update position'
      })
      .addCase(deletePosition.pending, (state) => {
        state.loading = true
      })
      .addCase(deletePosition.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false
        state.positions = state.positions.filter((pos) => pos.id !== action.payload)
      })
      .addCase(deletePosition.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) ?? 'Failed to delete position'
      })
      .addCase(duplicatePosition.pending, (state) => {
        state.loading = true
      })
      .addCase(duplicatePosition.fulfilled, (state, action: PayloadAction<Position>) => {
        state.loading = false
        state.positions.push(action.payload)
      })
      .addCase(duplicatePosition.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) ?? 'Failed to duplicate position'
      })
  }
})

export const { clearError, updateLocalPosition } = positionSlice.actions
export default positionSlice.reducer
